import { Component, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, OnInit, DestroyRef, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { EvCalculationService } from 'src/app/services/core/ev-calculation.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import device from 'current-device';
import { MapComponent } from 'src/app/components/map/map.component';
import { MapElementType } from 'src/app/models/map-element-type';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { filter, first, skip } from 'rxjs/operators';
import { NavigationService } from 'src/app/services/core/navigation.service';
import { WindPanelComponent } from 'src/app/components/ui-panels/wind-panel/wind-panel.component';
import { FeedbackComponent } from 'src/app/components/modals/info/feedback/feedback.component';
import { AccountService } from 'src/app/services/account/account.service';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { CarHandoffService } from 'src/app/services/platform/car-handoff.service';
import { startedFromCarHandoff } from 'src/app/services/platform/car-handoff-storage';
import { ConfirmationDialogComponent } from 'src/app/components/modals/confirmation-dialog/confirmation-dialog.component';
import { SettingsService } from 'src/app/services/account/settings.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { LanguageService } from 'src/app/services/account/language.service';
import NoSleep from 'nosleep.js';
import { MapService, ShowOnMapEnum } from 'src/app/services/map/map.service';
import { LocationService } from 'src/app/services/platform/location.service';
import { Waypoint } from '../../models/range-params';
import { WindElement } from 'src/app/models/wind';
import { HttpService } from 'src/app/services/core/http.service';
import { VehiclePanelComponent } from '../ui-panels/vehicle-panel/vehicle-panel.component';
import { CookiePanelComponent } from '../ui-panels/cookie-panel/cookie-panel.component';
import { DisplayMode } from 'src/app/models/display-mode';
import { Position } from 'src/app/models/position';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderPanelComponent } from '../ui-panels/header-panel/header-panel.component';
import { SearchPanelComponent } from '../ui-panels/search-panel/search-panel.component';
import { ProfilePanelComponent } from '../ui-panels/profile-panel/profile-panel.component';
import { AddressComponent } from '../ui-panels/address-panel/address-panel.component';
import { ChargerPanelComponent } from '../ui-panels/charger-panel/charger-panel.component';
import { HistoryPanelComponent } from '../ui-panels/history-panel/history-panel.component';
import { VoiceInputComponent } from '../voice-input/voice-input.component';
import { PlanningPanelComponent } from '../ui-panels/planning-panel/planning-panel.component';
import { NavigationHudComponent } from '../navigation/navigation-hud/navigation-hud.component';
import { TutorialComponent } from '../tutorial/tutorial.component';
import { CommonModule } from '@angular/common';
import { MapReadyService } from 'src/app/services/map/map-ready.service';
import { CalculationFlowHost, EvCalculationFlowHandler } from 'src/app/services/handlers/ev-calculation-flow-handler.service';
import { VoiceCommandFlowHandler } from 'src/app/services/handlers/voice-command-flow-handler.service';
import { NavigationLifecycleHandler, NavigationLifecycleHost } from 'src/app/services/handlers/navigation-lifecycle-handler.service';
import { RouterFlowHandler, RouterFlowHost } from 'src/app/services/handlers/router-flow-handler.service';
import { AccountFlowHandler } from 'src/app/services/handlers/account-flow-handler.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { MapUICoordinator, MapUICoordinatorHost } from 'src/app/services/map/map-ui-coordinator.service';
import { WidgetService } from 'src/app/services/platform/widget.service';
import { WidgetApiService } from 'src/app/services/platform/widget-api.service';
import { WidgetSettingsButtonComponent } from '../ui-components/widget-settings-button/widget-settings-button.component';

declare function addGtag(): any;

@Component({
  standalone: true,
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  imports: [MatDialogModule, CommonModule, HeaderPanelComponent, SearchPanelComponent, ProfilePanelComponent, 
    AddressComponent, ChargerPanelComponent, HistoryPanelComponent, VoiceInputComponent,
    PlanningPanelComponent, VehiclePanelComponent, MapComponent, WindPanelComponent,
    NavigationHudComponent, CookiePanelComponent, TutorialComponent, WidgetSettingsButtonComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainComponent implements OnInit, CalculationFlowHost, NavigationLifecycleHost, RouterFlowHost, MapUICoordinatorHost {

  @ViewChild('windPanelComponent') windPanelComponent?: WindPanelComponent;
  @ViewChild('vehiclePanel') VehiclePanelComponent?: VehiclePanelComponent;
  @ViewChild('map') mapComponent?: MapComponent;

  private noSleep = new NoSleep();

  public readonly selectedDisplayMode = signal<DisplayMode>(DisplayMode.RANGE);
  public readonly DisplayMode = DisplayMode;
  public readonly cookiePolicyAgreed = signal<boolean>(false);
  public readonly mobileResolution = signal<boolean>(false);
  public readonly mobileVisiblePanel = signal<string>("");
  public readonly mobileVisibleRoutePanel = signal<string>("routepoints");
  public readonly showOnMap = signal<{ type: MapElementType | ShowOnMapEnum, data?: any } | null>(null);
  private readonly destroyRef = inject(DestroyRef);
  private readonly vehicleService = inject(VehicleService);
  private readonly widgetService = inject(WidgetService);
  private readonly widgetApiService = inject(WidgetApiService);
  /** Embedded widget mode (widget host): no profile menu, no cookie banner, settings icon only. */
  public readonly widget = signal<boolean>(false);
  public readonly widgetSettings = signal<boolean>(false);
  /** False only in widget mode with carselector=0 and batteryslider=0 (panel would be empty). */
  public readonly vehiclePanelEnabled = signal<boolean>(true);
  public readonly loadingMap = signal<string | null>(null);
  public readonly iframeRedirect = signal<boolean>(false);
  public readonly demo = signal<boolean>(false);
  public readonly navigation = signal<boolean>(false);
  public readonly navigationRecalculateRoute = signal<boolean>(false);
  public readonly showVehiclePanel = signal<boolean>(false);
  public readonly lastWaypoints = signal<Waypoint[] | null>(null);
  public readonly failedPlan = signal<boolean>(false);
  public readonly noRoute = signal<boolean>(false);
  public readonly feedbackCounter = signal<number>(-1);
  public readonly modeBeforeHistory = signal<DisplayMode | null>(null);

  // subscribe to the coordinate change
  constructor(public mobileResolutionService: MobileResolutionService,
    public mapService: MapService,
    public matDialog: MatDialog,
    public waypointService: WaypointService,
    public evCalculationService: EvCalculationService,
    private cdr: ChangeDetectorRef,
    public utilsService: UtilsService,
    private navigationService: NavigationService,
    public webviewService: WebviewService,
    public accountService: AccountService,
    public settingsService: SettingsService,
    private stackedModalsService: StackedModalsService,
    public languageService: LanguageService,
    private locationService: LocationService,
    private httpService: HttpService,
    private mapReadyService: MapReadyService,
    private flowHandler: EvCalculationFlowHandler,
    private voiceCommandFlowHandler: VoiceCommandFlowHandler,
    private navLifecycleHandler: NavigationLifecycleHandler,
    private carHandoffService: CarHandoffService,
    private routerFlowHandler: RouterFlowHandler,
    private accountFlowHandler: AccountFlowHandler,
    private mapUICoordinator: MapUICoordinator) {

    this.routerFlowHandler.bind(this);

    this.widget.set(this.widgetService.isWidget());
    this.widgetSettings.set(this.widget() && this.widgetService.settingsEnabled());
    this.vehiclePanelEnabled.set(!this.widget() || this.widgetService.vehiclePanelEnabled());
    // Registers window.evWidget (gpsPositionUpdate, setBattery) + postMessage handling.
    this.widgetApiService.init();

    // The widget never shows the cookie banner: the embedding page owns consent.
    if (localStorage.getItem("cookiesAccepted") == "true" || this.webviewService.IsGPSTWebview() || this.widget()) {
      this.cookiePolicyAgreed.set(true);
    }

    if (!localStorage.getItem("language")) {
      localStorage.setItem("language", "en");
    }
    addGtag();
    const feedbackCounter = localStorage.getItem("feedbackCounter");
    if (feedbackCounter) {
      this.feedbackCounter.set(parseInt(feedbackCounter));
    }
    else {
      this.feedbackCounter.set(0);
    }

    // Cleared on an ordinary load because skipped chargers belong to one trip — but a load that
    // began with a car handoff is the same trip continuing, and clearing here would put the
    // chargers the driver just rejected back on the route the moment it replans.
    if (this.settingsService.acceptCookies && !startedFromCarHandoff()) {
      localStorage.setItem("skippedChargingStation", "null");
    }

    this.mobileResolution.set(this.mobileResolutionService.getMobileResolution());
    // subscribe variable changes

    this.waypointService.ObservableSelectedDisplayMode.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.selectedDisplayMode.set(resp);
      }
    });


  }

  // initalizing, subscribe to click event on the map
  ngOnInit(): void {
    // pre load audio files (voice commands; none in widget mode)
    if (this.webviewService.IsGPSTWebview() && !this.widget()) {
      this.httpService.initVoiceCommandAudio(this.utilsService.turnList);
    }

    this.mobileResolutionService.ObservableMobileResolution.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => { this.mobileResolution.set(res); });
    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((panelName) => {
      if (panelName != undefined) {
        this.mobileVisiblePanel.set(panelName);
      }
    });
  }

  ngAfterViewInit(): void {
    this.mapReadyService.mapReady$.subscribe(() => {
      document.getElementById("loading-screen")?.remove();
      this.onMapAndViewReady();
    });
  }

  private onMapAndViewReady(): void {
    this.mapComponent?.setChargingStations();
    this.mapComponent?.redrawCanvas();

    this.locationService.ObservableLastPosition.pipe(filter(value => value != null), first(), takeUntilDestroyed(this.destroyRef)).subscribe((position: Position) => {
      // A widget opened with an explicit lat/lon keeps that start point instead of
      // jumping to the device location.
      if (position && !(this.widget() && this.widgetService.getStartPosition())) {
        let lastPointDistance = Number.MAX_VALUE;
        if (this.waypointService.getWaypointsParams().length > 0) {
          lastPointDistance = this.utilsService.distanceBetweenCoordinates(
            [this.waypointService.getWaypointsParams()[0].lat, this.waypointService.getWaypointsParams()[0].lng],
            [position.Latitude, position.Longitude]);
        }
        if (this.webviewService.IsGPSTWebview()) {
          this.webviewService.InitGoToCurrentLocationFunction(() => { this.setStartCoordsToYourGeolocation(false, true) });
          if (lastPointDistance > 2000) {
            this.setStartCoordsToYourGeolocation(false, true);
          }

        } else {
          if (lastPointDistance > 2000) {
            this.setStartCoordsToYourGeolocation(false, true);
          }
        }
      }
    });

    // EvCalculationFlowHandler subscribes to EvCalculationService.state$ and
    // runs UI side effects against `this` (the bound host). bind() happens
    // here rather than in ngAfterViewInit because ViewChildren aren't strictly
    // required until the first non-idle emission, and any earlier emit gets
    // a guarded no-op via the handler's `if (!this.host) return`.
    this.flowHandler.bind(this);

    this.navLifecycleHandler.bind(this);

    // After navLifecycleHandler.bind, because an inbound car snapshot that was mid-navigation
    // hands control back through ObservableChangeNavigation and needs a listener already on it.
    this.carHandoffService.init();

    // Voice commands only come from the voice input, which widget mode does not have.
    if (!this.widget()) {
      this.voiceCommandFlowHandler.bind();
    }

    this.waypointService.ObservableSameCoordsError.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.openErrorDialog(this.languageService.languageJSON.App_ErrorDialog_SameStartEndLocation, "/assets/icons/" + this.settingsService.getMap().Skin + "/error/popup_icon_routeplanningfailed.svg");
      }
    });

    this.waypointService.ObservableReverseGeocodingLoader.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.setLoadingMap("circle");
      }
    });

    this.waypointService.ObservableNoRoute.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined && resp != null) {
        this.noRoute.set(resp);
        // Deleting waypoints down to a single start point in ROUTE mode no
        // longer triggers a calculation, so the previously planned route would
        // stay on the map. Clear it here (the cursor layer is left untouched).
        // RANGE mode still draws a range circle for one point, and navigation
        // must not be disturbed — so guard on ROUTE mode only.
        if (resp && this.selectedDisplayMode() === DisplayMode.ROUTE && !this.navigation()) {
          this.mapComponent?.clearMapLayers();
        }
      }
    });

    this.accountFlowHandler.bind();
    this.mapUICoordinator.bind(this);

    // ObservableBetterRoute "Replan accept" subscription moved into the
    // flow handler so the route-success side effects stay in one place.

    if (!this.webviewService.IsGPSTWebview() && !this.widget()) {
      this.accountService.initGoogleLogin();
    }
  }

  public get MapElementType(): typeof MapElementType {
    return MapElementType;
  }

  // ============================================================
  // CalculationFlowHost implementation — accessed by the bound
  // EvCalculationFlowHandler. See ev-calculation-flow-handler.service.ts.
  // ============================================================

  get vehiclePanelComponent(): VehiclePanelComponent { return this.VehiclePanelComponent!; }
  get isShowingMap(): boolean { return !!this.showOnMap(); }
  get isNavigating(): WritableSignal<boolean> { return this.navigation; }
  get isNavigationRecalculating(): WritableSignal<boolean> { return this.navigationRecalculateRoute; }

  public enableNoSleep(): void {
    if (device.mobile() && !this.webviewService.IsGPSTWebview && !device.ios()) {
      this.noSleep.enable();
    }
  }

  public disableNoSleep(): void {
    if (device.mobile() && !this.webviewService.IsGPSTWebview && !device.ios()) {
      this.noSleep.disable();
    }
  }

  public updateWindPanel(windElements: WindElement[], temperature: number, weathercode: number): void {
    const StartCoordsParams = this.waypointService.getStartCoordsParams();
    let closestIdx = 0;
    let closestDist = this.utilsService.distanceBetweenCoordinates(
      [StartCoordsParams.lat, StartCoordsParams.lng],
      [windElements[0].Location.lat, windElements[0].Location.lng],
    );
    for (let i = 1; i < windElements.length; i++) {
      const d = this.utilsService.distanceBetweenCoordinates(
        [StartCoordsParams.lat, StartCoordsParams.lng],
        [windElements[i].Location.lat, windElements[i].Location.lng],
      );
      if (d < closestDist) {
        closestIdx = i;
        closestDist = d;
      }
    }
    this.windPanelComponent?.setTemperature(temperature, weathercode, windElements[closestIdx].Angle, windElements[closestIdx].Speed);
  }

  public setMobileVisibleRoutePanel(panelName: string): void {
    this.mobileVisibleRoutePanel.set(panelName);
    this.mobileResolutionService.setMobileVisibleRoutePanel(panelName);
  }

  // ============================================================
  // Calculation pipeline + side-effects: moved to
  //   EvCalculationService              — pipeline, HTTP, error mapping
  //   EvCalculationFlowHandler          — state$ → UI side effects
  // ============================================================

  private setLoadingMap(loading: string | null): void {
    if (this.loadingMap() == "line" && loading != "line" && !this.navigation()) {
      this.mapComponent?.clearMapLayers();
    }
    this.loadingMap.set(loading);
  }

  public loadingHistoryChanged(loading: boolean): void {
    if (loading) {
      this.setLoadingMap("circle");
    }
    else {
      this.setLoadingMap(null);
    }
  }

  //Mobile resolution
  public toggleVehiclePanel(): void {
    this.showVehiclePanel.update(visible => !visible);
  }

  public setMobileVisiblePanel(panelName: string): void {
    // reposition map during opening/closing car sliders
    if (panelName === "car" && !this.navigation()) {
      this.mapComponent?.positionMapOnSlidersPanelOpen();
    }
    if ((this.mobileVisiblePanel() === "car" || this.mobileVisiblePanel() === "car-open") && !this.navigation()) {
      this.mapComponent?.positionMapOnSlidersPanelClose();
    }
    this.mobileVisiblePanel.set(panelName);
    this.mobileResolutionService.setMobileVisiblePanel(panelName);
  }

  // openSkipChargingStationFailedDialog moved to EvCalculationFlowHandler
  // (only the calculation error path opened it).

  // openErrorDialog is still used by ObservableSameCoordsError (a user-input
  // validation, not a calculation outcome) so it stays here. The handler
  // has its own copy for calculation errors.

  private openErrorDialog(title: string, icon: string, message: string | null = null): void {
    if ((!this.navigation() || this.navigationRecalculateRoute()) &&
      document.getElementsByClassName("mat-dialog-title").length == 0) {
      let war: string | null = this.selectedDisplayMode() == DisplayMode.ROUTE && (this.vehicleService.getECarSettings()?.batteryLevel ?? 100) < 10 ? this.languageService.languageJSON.App_WarningLowBattery : null;
      this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, { data: { title: title, icon: icon, message: message, warning: war, buttons: [this.languageService.languageJSON.Global_Ok, null] }, autoFocus: false });

      if (this.navigation()) {
        this.navigationService.ObservableChangeNavigation.next("exit");
      }
    }
  }

  public openFeedbackDialog(): void {
    this.stackedModalsService.openModal(FeedbackComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public setStartCoordsToYourGeolocation(setByUserClick?: boolean, firstPosition?: boolean): void {
    if (!this.navigation() && this.locationService.ObservableLastPosition?.value) {
      const startcoord = new Waypoint(
        this.locationService.ObservableLastPosition.value.Latitude,
        this.locationService.ObservableLastPosition.value.Longitude
      );
      if (this.mapComponent) {
        if (!this.mapComponent.getFollowModeRange() || firstPosition) {
          if (firstPosition) {
            // Cancellation is automatic now — switchMap in EvCalculationService
            // discards any in-flight calc when the next combineLatest emit
            // arrives. paramsUpdate forces a re-emit.
            this.waypointService.triggerParamsUpdate()
          }
          this.mapComponent.setFollowModeRange(true);
          const newWaypoints = this.waypointService.getWaypointsParams();
          newWaypoints[0] = startcoord;
          this.waypointService.setWaypointParams(newWaypoints);
          this.mapComponent.mapInstance?.flyTo({ center: [startcoord.lng, startcoord.lat] });
        }
        else if (setByUserClick) {
          this.mapComponent.userTriggeredMapFly(startcoord);
        }
        else {
          this.mapComponent.mapInstance?.flyTo({ center: [startcoord.lng, startcoord.lat] });
        }
      }

      this.mapService.getReverseGeocode(startcoord.lat, startcoord.lng).subscribe({
        next: (reversegeocode) => {
          if (reversegeocode) {
            this.waypointService.setStartDisplayName(reversegeocode);
          }
        },
        error: () => { }
      });

    }
    else {
      this.mapComponent?.setToCenter();
    }
  }
  public acceptCookies(): void {
    this.cookiePolicyAgreed.set(true);
    localStorage.setItem("cookiesAccepted", "true");
  }

  ngOnDestroy() {
    this.routerFlowHandler.unbind();
    this.accountFlowHandler.unbind();
    this.mapUICoordinator.unbind();
    this.flowHandler.unbind();
    this.voiceCommandFlowHandler.unbind();
    this.navLifecycleHandler.unbind();
    if (this.mapComponent?.mapInstance) {
      const center = this.mapComponent.mapInstance.getCenter();
      this.mapService.setLastCoordinates(center.lat, center.lng);
    }
  }
}
